import { useState } from "react";

import { ApiError } from "@/lib/api/client";
import {
  usePatientRotationPreview,
  useRotatePatients,
} from "@/lib/api/clinicianHooks";
import type { ClinicalTeamMember } from "@/types/clinician";

/**
 * Moving a prescriber's patients to prescribers licensed in each patient's
 * state, in batches until every patient who can move has moved; patients no
 * licensed prescriber can take stay and are counted by state. Two modes:
 * - Unlicensed states: only patients in states the prescriber isn't
 *   licensed in (a lost or paused license). Any supervisor; the prescriber
 *   keeps everyone else and stays in routing.
 * - All patients: rotating a departing prescriber off. Full admin only, and
 *   the prescriber must stop accepting new patients first.
 */
export function PrescriberRotation({
  member,
  fullAdmin,
}: {
  member: ClinicalTeamMember;
  fullAdmin: boolean;
}) {
  const [mode, setMode] = useState<"unlicensed" | "all">("unlicensed");
  const unlicensed = mode === "unlicensed";
  const preview = usePatientRotationPreview(member.clinician_id, true, unlicensed);
  const rotate = useRotatePatients(member.clinician_id, unlicensed);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<{
    moved: number;
    kept: Record<string, number>;
    done: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const name = member.display_name || member.email || "this prescriber";
  const rows = preview.data?.states ?? [];
  const total = rows.reduce((sum, row) => sum + row.patients, 0);
  const stranded = rows
    .filter((row) => row.eligible_prescribers === 0)
    .reduce((sum, row) => sum + row.patients, 0);

  const run = async () => {
    setError(null);
    setConfirming(false);
    let after: string | null = null;
    let moved = 0;
    const kept: Record<string, number> = {};
    setProgress({ moved, kept, done: false });
    try {
      do {
        const batch = await rotate.mutateAsync({ afterPatientId: after });
        moved += batch.moved;
        for (const [state, count] of Object.entries(batch.kept_by_state)) {
          kept[state] = (kept[state] ?? 0) + count;
        }
        setProgress({ moved, kept: { ...kept }, done: false });
        after = batch.last_patient_id;
      } while (after);
      setProgress({ moved, kept: { ...kept }, done: true });
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "The move stopped. Patients already moved keep their new prescriber; run it again to continue.",
      );
      setProgress((current) => (current ? { ...current, done: true } : null));
    }
  };

  const canRun = unlicensed || fullAdmin;

  return (
    <div className="flex flex-col gap-8">
      <div role="tablist" className="flex flex-wrap gap-4">
        {(
          [
            ["unlicensed", "States they aren’t licensed in"],
            ["all", "All patients (rotate off)"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => {
              setMode(value);
              setConfirming(false);
              setProgress(null);
              setError(null);
            }}
            className={`rounded border px-8 py-5 type-label-xs ${
              mode === value
                ? "border-ink bg-ink text-page"
                : "border-line text-ink-muted hover:border-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="type-body-sm text-ink-muted">
        {unlicensed
          ? `${name}'s ${total} patients in states they aren't licensed in, for example after a license lapses or while a supervising physician is pending. They move to a licensed prescriber; ${name} keeps everyone else and stays in routing.`
          : `Preview of moving all ${total} of ${name}'s assigned patients through licensed routing. Nothing moves until a full admin confirms.`}
      </p>
      {preview.isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading preview…</p>
      ) : preview.isError ? (
        <p className="type-body-sm text-danger">
          The preview could not be loaded.
        </p>
      ) : rows.length ? (
        <div className="max-h-[22rem] overflow-auto rounded border border-line bg-page">
          <table className="w-full type-body-sm">
            <thead className="sticky top-0 bg-sunken text-left type-label-xs text-ink-muted">
              <tr>
                <th className="px-10 py-6">State</th>
                <th className="px-10 py-6 text-right">Patients</th>
                <th className="px-10 py-6 text-right">
                  Licensed prescribers accepting
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {rows.map((row) => (
                <tr key={row.state}>
                  <td className="px-10 py-5">
                    {row.state === "unknown" ? "Unknown" : row.state}
                  </td>
                  <td className="px-10 py-5 text-right tabular-nums">
                    {row.patients}
                  </td>
                  <td
                    className={`px-10 py-5 text-right tabular-nums ${row.eligible_prescribers ? "" : "text-danger"}`}
                  >
                    {row.eligible_prescribers}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="type-body-sm text-ink-muted">
          {unlicensed
            ? "No patients in states they aren’t licensed in."
            : "No assigned patients."}
        </p>
      )}
      {stranded > 0 && (
        <p className="type-body-sm text-warning">
          {stranded} patients are in states no other accepting prescriber is
          licensed for. They would stay with {name}.
        </p>
      )}

      {canRun && total > 0 && (
        <div className="flex flex-wrap items-center gap-8">
          {!unlicensed && member.accepting_new_patients ? (
            <p className="type-body-sm text-ink-muted">
              Turn off “Accepting new patients” for {name} before moving their
              patients.
            </p>
          ) : confirming ? (
            <>
              <button
                type="button"
                disabled={rotate.isPending}
                onClick={run}
                className="rounded bg-danger px-12 py-7 type-label-sm text-ink-onAccent disabled:opacity-40"
              >
                Move {total - stranded} patients now
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded border border-line px-10 py-6 type-label-sm hover:bg-page"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={rotate.isPending}
              onClick={() => setConfirming(true)}
              className="rounded border border-danger/40 px-10 py-6 type-label-sm text-danger hover:bg-danger/5 disabled:opacity-40"
            >
              Move patients to licensed prescribers…
            </button>
          )}
        </div>
      )}
      {progress && (
        <p className="type-body-sm text-ink-muted" role="status">
          {progress.done ? "Finished." : "Moving…"} {progress.moved} moved
          {Object.keys(progress.kept).length
            ? ` · stayed (no licensed prescriber): ${Object.entries(
                progress.kept,
              )
                .map(([state, count]) => `${state} ${count}`)
                .join(", ")}`
            : ""}
        </p>
      )}
      {error && <p className="type-body-sm text-danger">{error}</p>}
    </div>
  );
}
